import {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useImmerReducer } from 'use-immer';
import { ThemeColor, legacyTheme } from 'styles/theme';

export interface Notification {
  isError?: boolean;
  message?: string;
  messageTx?: string;
  timeout?: number;
}

interface State extends Notification {
  preloaderBackgroundColor?: ThemeColor;
  preloaderVisible?: boolean;
  visible?: boolean;
}

const initialState: State = {
  isError: false,
  visible: false,
};

type Action =
  | { data: State; type: 'SHOW_NOTIFICATION' }
  | { type: 'HIDE_NOTIFICATION' }
  | { background?: ThemeColor; type: 'SHOW_PRELOADER' }
  | { type: 'HIDE_PRELOADER' };

function notificationReducer(draft: State, action: Action) {
  switch (action.type) {
    case 'SHOW_NOTIFICATION':
      draft = { ...draft, ...action.data, visible: true };
      return draft;
    case 'HIDE_NOTIFICATION':
      draft.visible = false;
      draft.message = '';
      draft.messageTx = '';
      return draft;
    case 'SHOW_PRELOADER':
      draft.preloaderVisible = true;
      draft.preloaderBackgroundColor = action.background;
      return draft;
    case 'HIDE_PRELOADER':
      draft.preloaderVisible = false;
      return draft;
  }
}

const NotificationStateContext = createContext<State>(initialState);

const NotificationDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined,
);

export const NotificationsProvider = (props: { children: ReactNode }) => {
  const { children } = props;

  const [state, dispatch] = useImmerReducer(notificationReducer, initialState);

  return (
    <NotificationStateContext.Provider value={state}>
      <NotificationDispatchContext.Provider value={dispatch}>
        {children}
      </NotificationDispatchContext.Provider>
    </NotificationStateContext.Provider>
  );
};

export function useNotificationState(): State {
  const context = useContext(NotificationStateContext);

  if (context === undefined) {
    throw new Error(
      `useNotification must be used inside an NotificationsProvider`,
    );
  }

  return context;
}

export function useNotificationDispatch() {
  const context = useContext(NotificationDispatchContext);

  if (context === undefined) {
    throw new Error(
      `useNotification must be used inside an NotificationsProvider`,
    );
  }

  return context;
}

const PRELOADER_WAITING_TIME = 500;

export const useNotification = () => {
  const state = useNotificationState();
  const dispatch = useNotificationDispatch();
  const waitingToShowPreloader = useRef(false);

  const notify = useCallback(
    (notification: string | Notification) => {
      const data =
        typeof notification === 'string'
          ? {
              isError: false,
              message: notification,
            }
          : { isError: false, ...notification };

      dispatch({ data, type: 'SHOW_NOTIFICATION' });
    },
    [dispatch],
  );

  const notifyError = useCallback(
    (notification: string | Notification) => {
      const data =
        typeof notification === 'string'
          ? {
              isError: true,
              message: notification,
            }
          : { isError: true, ...notification };

      dispatch({ data, type: 'SHOW_NOTIFICATION' });
    },
    [dispatch],
  );

  const hideNotification = useCallback(() => {
    dispatch({ type: 'HIDE_NOTIFICATION' });
  }, [dispatch]);

  const showPreloader = useCallback(
    (background?: ThemeColor) => {
      waitingToShowPreloader.current = true;

      setTimeout(() => {
        if (waitingToShowPreloader.current) {
          dispatch({ background, type: 'SHOW_PRELOADER' });
        }
      }, PRELOADER_WAITING_TIME);
    },
    [dispatch, waitingToShowPreloader],
  );

  const hidePreloader = useCallback(() => {
    waitingToShowPreloader.current = false;
    dispatch({ type: 'HIDE_PRELOADER' });
  }, [dispatch]);

  const togglePreloader = useCallback(() => {
    if (state.preloaderVisible) {
      hidePreloader();
    } else {
      showPreloader();
    }
  }, [state.preloaderVisible, showPreloader, hidePreloader]);

  return {
    ...state,
    hideNotification,
    hidePreloader,
    notify,
    notifyError,
    showPreloader,
    togglePreloader,
  };
};
